import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { ui } from '@procuros/datachecks';
import { ColumnDef, ColumnMeta } from '@tanstack/react-table';
import { produce } from 'immer';

import { ColumnTypesInternal } from '../../Types';
import { flattenMultiColumnColumns, setBulkValue } from './helpers';

export type Props<TData, TValue> = {
  columns: Array<ColumnDef<TData, TValue>>;
  rowSelection: Record<string, unknown>;
  tableData: Array<TData>;
  setTableData: Dispatch<SetStateAction<Array<TData>>>;
};

export const useBulkActions = <TData, TValue>({
  columns,
  rowSelection,
  tableData,
  setTableData,
}: Props<TData, TValue>) => {
  const [isBulkEditModalOpen, setBulkEditModalOpen] = useState(false);
  const handleBulkEditModalClose = useCallback(() => {
    setBulkEditModalOpen(false);
  }, []);

  const handleBulkEditButtonClick = useCallback(() => {
    setBulkEditModalOpen(true);
  }, []);

  const bulkColumns = useMemo(() => {
    return flattenMultiColumnColumns(columns).filter(
      (columnMeta) =>
        columnMeta &&
        !columnMeta.readOnly &&
        columnMeta.type &&
        !(columnMeta.type in ColumnTypesInternal) &&
        columnMeta.type !== ui.FieldTypes.multi_column,
    );
  }, [columns]) as Array<ColumnMeta<TData, TValue> & { id: string }>;

  const handleBulkEdit = useCallback(
    (column?: string, value?: string | number) => {
      const selection = Object.keys(rowSelection).map((item) => parseInt(item, 10));

      const nextState = produce(tableData, (nextData) => {
        selection.forEach((index: number) => {
          setBulkValue(nextData[index], column as string, value as string | number);
        });
      }) as Array<TData>;

      setTableData(nextState);
    },
    [rowSelection, tableData, setTableData],
  );

  return { isBulkEditModalOpen, handleBulkEditModalClose, bulkColumns, handleBulkEdit, handleBulkEditButtonClick };
};
